import { m } from "@/paraglide/messages.js";
import { Button } from "@/components/ui/button.tsx";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { fmtDateUtc } from "@/shared/format.ts";
import { Ban, CalendarClock, LogOut } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useAuth } from "../auth.tsx";
import { pageTitle } from "@/utils/page-title.ts";

/** How often a waiting member's page asks again whether their term has started. */
const RECHECK_MS = 5 * 60 * 1000;

/**
 * Where a member of a suspended organization lands.
 *
 * Deliberately NOT `/pricing`. A lapsed subscription is something the member can fix by
 * paying; a suspension is not, so a checkout page would be a dead end that also implies
 * the outage is their billing problem. This screen says what happened, says who can undo
 * it, and offers nothing else.
 *
 * OUTSIDE the `_authenticated` layout on purpose: that layout's guard is what redirects
 * here, so rendering inside it would loop. It also means no app chrome — no sidebar into
 * spaces the member can no longer open.
 *
 * There is no support link or contact button. Who to contact depends on the customer's own
 * arrangement with their partner, and inventing a mailto: here would send people to the
 * wrong place.
 *
 * SIGN OUT IS THE ONE ACTION, and leaving it off made this screen a trap. A person belongs
 * to exactly one org — `users.org_id` is a single non-null FK under `UNIQUE (email, org_id)`
 * — so someone in two organizations holds two SEPARATE accounts, and signing out is the
 * only route to the other one. Without it, suspending one tenant locked its members out of
 * an unrelated login they still had every right to use. `/pricing`, the same shape of dead
 * end for an unpaid org, has carried this since before the freeze work existed; this is the
 * same handler.
 */
export const Route = createFileRoute("/suspended")({
	beforeLoad: ({ context }) => {
		if (!context.auth.isAuthenticated) {
			throw redirect({ to: "/auth/login" });
		}
		// A member whose org was restored while they sat on this page must not be stuck on
		// it. The guard sends them back here if it is still frozen, so this cannot loop.
		if (context.auth.user && !context.auth.user.isOrgFrozen) {
			throw redirect({ to: "/" });
		}
	},
	head: () => ({ meta: [{ title: pageTitle(m.org_suspended_title()) }] }),
	component: SuspendedRoute,
});

function SuspendedRoute() {
	const { auth } = Route.useRouteContext();
	const { logout } = useAuth();
	const navigate = useNavigate();
	// Navigate AFTER logout, exactly as `pricing-page.tsx` does: `logout` commits the
	// unauthenticated state with `flushSync`, so the router context this callback's
	// navigate sees is already signed out and the login route's guard cannot bounce back.
	const handleLogout = useCallback(() => {
		logout();
		void navigate({ to: "/auth/login" });
	}, [logout, navigate]);
	// ── NOT SUSPENDED: WAITING ───────────────────────────────────────────────
	//
	// The same `frozen_at` sends a member of a contract organization here while its
	// term has not started. (Nobody NEW gets here: invitations are refused until the
	// organization wakes — `auth/invite-setup.ts`.) Telling them their
	// organization "has been suspended" is false, alarming, and points them at a
	// problem that does not exist — their first experience of a product they have
	// paid for. The date is the whole answer, so it is the headline.
	const startsAt = auth.user?.contractStartsAt;
	// STUCK rather than waiting. The date has passed and nobody approved the term, so
	// a countdown would point at a day in the past — which reads as a broken product
	// rather than as a decision somebody owes them.
	const awaitingApproval = auth.user?.contractAwaitingApproval === true;
	// APPROVED AND DUE: the hourly activation has simply not run yet. Showing "becomes
	// active on <a past date>" here read as broken; the honest answer is "any minute".
	const starting =
		startsAt !== undefined &&
		auth.user?.contractApproved === true &&
		new Date(startsAt).getTime() <= Date.now();
	// NOT CONFIRMED: a future term nobody has approved — which a member only sees
	// when approval was withdrawn after they set up. It will not start on its date
	// unless it is approved first, so a plain countdown would promise too much.
	const unconfirmed =
		startsAt !== undefined &&
		auth.user?.contractApproved === false &&
		!awaitingApproval;

	// RE-CHECK WHILE WAITING. Nothing else on this page ever asks again, so a member
	// who kept it open through their start date stayed on "not started" until they
	// thought to reload. A reload re-runs the session check, and the route guard
	// above sends a woken organization straight to the app. Never for a suspension —
	// nothing about waiting clears one.
	useEffect(() => {
		if (startsAt === undefined) {
			return;
		}
		const timer = window.setInterval(
			() => window.location.reload(),
			RECHECK_MS,
		);
		return () => window.clearInterval(timer);
	}, [startsAt]);

	return (
		<div className="flex min-h-svh items-center justify-center bg-background p-6">
			<div className="w-full max-w-md space-y-4 text-center">
				<div className="mx-auto flex size-12 items-center justify-center rounded-full bg-muted">
					{startsAt === undefined ? (
						<Ban className="size-6 text-muted-foreground" aria-hidden="true" />
					) : (
						<CalendarClock
							className="size-6 text-muted-foreground"
							aria-hidden="true"
						/>
					)}
				</div>
				<h1 className="font-semibold text-2xl text-foreground">
					{startsAt === undefined
						? m.org_suspended_title()
						: awaitingApproval
							? m.org_awaiting_activation_title()
							: starting
								? m.org_activating_title()
								: m.org_not_started_title()}
				</h1>
				<p className="text-muted-foreground text-sm">
					{startsAt !== undefined
						? awaitingApproval
							? m.org_awaiting_activation_body()
							: starting
								? m.org_activating_body()
								: unconfirmed
									? m.org_not_confirmed_body({ date: fmtDateUtc(startsAt) })
									: m.org_not_started_body({ date: fmtDateUtc(startsAt) })
						: auth.user?.orgName === undefined
							? m.org_suspended_body()
							: m.org_suspended_body_named({ orgName: auth.user.orgName })}
				</p>
				<p className="text-muted-foreground text-sm">
					{startsAt === undefined
						? m.org_suspended_contact()
						: awaitingApproval
							? m.org_awaiting_activation_hint()
							: starting
								? m.org_waiting_rechecks()
								: m.org_not_started_hint()}
				</p>
				<Button variant="outline" onClick={handleLogout}>
					<LogOut className="mr-2 size-4" />
					{m.common_logout()}
				</Button>
			</div>
		</div>
	);
}
